/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import AppTable from 'components/appTable';
import Empty from 'components/empty';
import SearchBox from 'components/form/searchBox';
import PanelBoxLayout from 'components/layout/panelBox';
import Toolbar, { ToolbarRight } from 'components/toolbar';
import { useActions } from 'common/hooks/useActions';
import PaginationApp from 'components/pagination';
import RowAction from 'components/rowAction';

import StatusChip from 'components/statusChip';
import Value from 'components/value';
import { MISSING } from 'common/helpers/format';

import * as S from './styles';
import * as T from 'common/types';
import AdminFilterPopup from '../filterForm';

interface IProps {
  data: { list: []; totalPages?: number };
  loading: boolean;
  roles: string[];
}

/* Same act, same icon as the user list — see the note there. */
const RoleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

export default function AdminListTable({ data, roles, loading }: IProps) {
  const { usersRequestAction } = useActions();
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    search: '',
    type: '',
    role: ['admin', 'sAdmin', 'supporter', 'cafeTeriaManager']
  });

  const fetchData = () => {
    usersRequestAction(meta);
  };

  useEffect(() => {
    fetchData();
  }, [meta]);

  const handleChangePage = (page: number) => {
    setMeta({ ...meta, page });
  };

  const handleChangePageSize = (page_size: number) => {
    setMeta({ ...meta, page_size });
  };

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          <SearchBox
            onChange={(e) => setMeta({ ...meta, search: e, page: 1 })}
            placeholder="Search admins"
            value={meta.search}
          />
          <ToolbarRight>
            <AdminFilterPopup setFilter={setMeta} filter={meta} roles={roles} />
          </ToolbarRight>
        </Toolbar>
        <AppTable
          headers={[
            { id: 1, key: 'id', title: 'Admin ID', dir: 'left', width: 80 },
            { id: 4, key: 'name', title: 'User Name', dir: 'left' },
            { id: 5, key: 'email', title: 'E-mail', dir: 'left' },
            { id: 6, key: 'phone', title: 'Phone', dir: 'left', width: 125 },
            { id: 7, key: 'department', title: 'Department', dir: 'left', width: 110 },
            { id: 9, key: 'jobTitle', title: 'Job Title', dir: 'left', width: 110 },
            { id: 8, key: 'role', title: 'Role', dir: 'left', width: 140 },
            { id: 10, key: 'edit', title: '', type: 'actions', width: 64 }
          ]}
          data={data.list}
          empty={
            meta.search ? (
              <Empty
                title={`No admins match “${meta.search}”`}
                text="Check the spelling, or clear the search to see every admin."
              />
            ) : (
              <Empty title="No admins yet" text="Give a user an admin role and they appear in this list." />
            )
          }
          loading={loading}
          altanfeethiFields={{
            id: (item: T.Pages.UserItem) => (
              <S.Div className="text-left">
                <Value mono>{item?.id}</Value>
              </S.Div>
            ),
            name: (item: T.Pages.UserItem) => (
              <S.Div className="text-left captal">
                <Value fallback={MISSING.provided}>{item?.displayName}</Value>
              </S.Div>
            ),
            email: (item: T.Pages.UserItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>{item?.mail}</Value>
              </S.Div>
            ),
            phone: (item: T.Pages.UserItem) => (
              <S.Div className="text-left">
                <Value mono fallback={MISSING.none}>
                  {item?.mobilePhone}
                </Value>
              </S.Div>
            ),
            department: (item: T.Pages.UserItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.none}>{item?.department}</Value>
              </S.Div>
            ),
            jobTitle: (item: T.Pages.UserItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.none}>{item?.jobTitle}</Value>
              </S.Div>
            ),
            role: (item: T.Pages.UserItem) => (
              <S.Div className="text-left">
                <StatusChip domain="role" value={item?.role} fallback={<Value />} />
              </S.Div>
            ),
            edit: (item: T.Pages.UserItem) => (
              <RowAction
                to={`/panel/users/change-role/${item.id}`}
                label={`Change role for ${item?.displayName || 'this admin'}`}
              >
                <RoleIcon />
              </RowAction>
            )
          }}
        />
      </PanelBoxLayout>
      {data.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.page_size * data.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={data.totalPages}
          pageSize={meta.page_size}
          handleChangeTake={handleChangePageSize}
        />
      ) : (
        <></>
      )}
    </S.Content>
  );
}
